import { Component, inject, output, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SiFileUploaderComponent, UploadFile } from '@siemens/element-ng/file-uploader';

import { BROUTER_MAP_CONFIG } from '../../config/brouter-map.config';
import { LatLng } from '../../models/brouter-map.model';
import {
  DEFAULT_MAX_WAYPOINTS,
  detectFormat,
  parseTrackFile,
  simplifyToMaxPoints
} from '../../services/track-loader';

export interface LoadedTrack {
  points: LatLng[];
  /** Points in the file before simplification, shown so the reduction is visible. */
  originalCount: number;
  name: string;
}

/**
 * Imports a GPX, KML or GeoJSON file as an editable route.
 *
 * Replaces the legacy "Track as Route" dialog. That dialog exposed a tolerance slider;
 * here the tolerance is derived from a waypoint ceiling instead, since the ceiling is the
 * constraint that actually matters to the routing engine.
 */
@Component({
  selector: 'app-track-loader-dialog',
  imports: [TranslateModule, SiFileUploaderComponent],
  templateUrl: './track-loader-dialog.component.html'
})
export class TrackLoaderDialogComponent {
  readonly trackLoaded = output<LoadedTrack>();
  readonly closed = output<void>();

  private readonly config = inject(BROUTER_MAP_CONFIG);
  private readonly translateService = inject(TranslateService);

  readonly maxWaypoints = DEFAULT_MAX_WAYPOINTS;
  /** `trackSizeLimit` is in KB; the uploader wants bytes. */
  readonly maxFileSize = this.config.trackSizeLimit * 1024;
  readonly error = signal<string | null>(null);

  async onFilesChanged(files: UploadFile[]): Promise<void> {
    const selected = files.at(0);
    if (!selected) {
      this.error.set(null);
      return;
    }

    // Size and type rejections arrive here as an 'invalid' file carrying its own message.
    if (selected.status === 'invalid') {
      this.error.set(this.translateService.instant(selected.errorText as string));
      return;
    }

    this.error.set(null);

    const format = detectFormat(selected.fileName);
    if (!format) {
      this.error.set(this.translateService.instant('BROUTER_MAP.LOAD.UNSUPPORTED'));
      return;
    }

    try {
      const points = parseTrackFile(await selected.file.text(), format);

      if (points.length < 2) {
        this.error.set(this.translateService.instant('BROUTER_MAP.LOAD.NO_TRACK'));
        return;
      }

      const simplified = simplifyToMaxPoints(points, this.maxWaypoints);
      this.trackLoaded.emit({
        points: simplified.points,
        originalCount: points.length,
        name: selected.fileName.replace(/\.[^.]+$/, '')
      });
    } catch {
      this.error.set(this.translateService.instant('BROUTER_MAP.LOAD.PARSE_FAILED'));
    }
  }
}
